import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { useBannerContext } from '@xing-com/crate-jobs-components-banner';
import { InterviewBookingRequestDocument } from '@xing-com/crate-jobs-graphql';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import { useViewerData } from '@xing-com/hub';
import { IconClock, IconEvents } from '@xing-com/icons';
import { IllustrationStickerRockHand } from '@xing-com/illustrations';
import { mediaConfined } from '@xing-com/layout-tokens';
import { PopUpWindow } from '@xing-com/pop-up-window';
import { spaceXXL } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

import { useBookInterviewButtonTracking } from './use-book-interview-button-tracking';

const InterviewBookingButton = styled(Button)<{ $isHeaderSticky: boolean }>`
  width: 100%;

  @media ${mediaConfined} {
    width: ${({ $isHeaderSticky }) =>
      $isHeaderSticky ? 'max-content' : '100%'};
  }
`;

const PopUpContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StyledButton = styled(Button)`
  margin-top: ${spaceXXL};
  width: 100%;
`;

type Props = {
  jobId: string;
  application: ApplicationData;
};

const EXIT_SURVEY_URL =
  'https://survey2.quantilope.com/SKwZRETCtBToPbeQt/hqR22zv5wghJLGQwB';

export const BookInterviewButton: React.FC<Props> = ({
  jobId,
  application,
}: Props) => {
  const { data: viewerData } = useViewerData();
  const [isShown, setIsShown] = useState(false);
  const { showErrorBanner } = useBannerContext();
  const [requestInterviewBooking, { loading }] = useMutation(
    InterviewBookingRequestDocument,
    {
      variables: { input: { jobId } },
      onCompleted: (data) => {
        if (data.jobBookingRequest?.bookingUrl) {
          window.open(data.jobBookingRequest.bookingUrl, '_self', 'noopener');
        }
      },
      onError: () => {
        showErrorBanner('JOBS_JOB_DETAILS_INTERVIEW_BOOKING_ERROR');
      },
    }
  );
  const track = useBookInterviewButtonTracking({
    jobId,
    applicationType: application.type,
  });

  const show = (): void => {
    setIsShown(true);
    track({ action: 'start' });
  };

  const hide = (): void => {
    setIsShown(false);
    track({ action: 'exit' });
    const hashedUserId = viewerData?.hashedUserId;
    if (hashedUserId) {
      window.open(`${EXIT_SURVEY_URL}?h=${hashedUserId}`, '_blank', 'noopener');
    }
  };

  const bookInterview = (): void => {
    requestInterviewBooking();
    track({ action: 'interview' });
  };

  return (
    <>
      {/* @ts-expect-error FIXME: SC6 */}
      <InterviewBookingButton
        variant="primary"
        size="medium"
        icon={IconEvents}
        onClick={show}
      >
        <FormattedMessage id="JOBS_JOB_DETAILS_BOOK_INTERVIEW" />
      </InterviewBookingButton>
      <PopUpWindow isShown={isShown} hide={hide}>
        <PopUpContainer>
          <IllustrationStickerRockHand size="xlarge" />
          <Headline size="xlarge">
            <FormattedMessage id="JOBS_JOB_DETAILS_BOOK_INTERVIEW_HEADLINE" />
          </Headline>
          <BodyCopy size="small">
            <FormattedMessage id="JOBS_JOB_DETAILS_BOOK_INTERVIEW_BODY" />
          </BodyCopy>
          <StyledButton
            variant="primary"
            loading={loading}
            icon={IconClock}
            onClick={bookInterview}
          >
            <FormattedMessage id="JOBS_JOB_DETAILS_BOOK_INTERVIEW" />
          </StyledButton>
        </PopUpContainer>
      </PopUpWindow>
    </>
  );
};
